/**
 * 요구사항·이슈 히스토리 탭 (읽기 전용).
 *
 * 발주처 요구사항 / 반복 이슈 / 확인 대기 문의를 한 곳에 모아 주기적으로
 * 확인한다. 데이터는 ``./data`` 의 ``REQUIREMENTS_HISTORY`` 에서 온다.
 */

import { ClipboardList, ListChecks } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

import {
  REQUIREMENTS_HISTORY,
  REVIEW_CHECKLIST,
  type RequirementCategory,
  type RequirementStatus,
  pickLang,
} from "./data";

function CategoryBadge({ category }: { category: RequirementCategory }) {
  const { t } = useTranslation();
  const cls: Record<RequirementCategory, string> = {
    requirement: "bg-blue-100 text-blue-700",
    issue: "bg-amber-100 text-amber-700",
    inquiry: "bg-violet-100 text-violet-700",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        cls[category],
      )}
    >
      {t(`guide.category.${category}`)}
    </span>
  );
}

function StatusBadge({ status }: { status: RequirementStatus }) {
  const { t } = useTranslation();
  const cls: Record<RequirementStatus, string> = {
    done: "bg-emerald-100 text-emerald-700",
    in_progress: "bg-sky-100 text-sky-700",
    pending: "bg-rose-100 text-rose-700",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        cls[status],
      )}
    >
      {t(`guide.status.${status}`)}
    </span>
  );
}

export default function RequirementsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <ClipboardList className="h-5 w-5 text-slate-400" />
          {t("guide.page.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("guide.page.description")}</p>
      </div>

      {/* 검수 체크리스트 요약 (홈과 동일 내용, 여기서도 참조 가능) */}
      <section className="mb-8 rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <ListChecks className="h-4 w-4 text-blue-500" />
          {t("guide.checklist.title")}
        </h2>
        <ol className="space-y-1.5">
          {REVIEW_CHECKLIST.map((item) => (
            <li key={item.id} className="flex gap-2 text-sm text-slate-700">
              <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500">
                {item.id}
              </span>
              <span>{pickLang(item.text, lang)}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* 요구사항·이슈 히스토리 표 */}
      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="whitespace-nowrap px-4 py-2.5 font-medium">{t("guide.table.date")}</th>
              <th className="whitespace-nowrap px-4 py-2.5 font-medium">
                {t("guide.table.category")}
              </th>
              <th className="px-4 py-2.5 font-medium">{t("guide.table.item")}</th>
              <th className="whitespace-nowrap px-4 py-2.5 font-medium">
                {t("guide.table.status")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {REQUIREMENTS_HISTORY.map((entry) => (
              <tr key={entry.id} className="align-top hover:bg-slate-50/60">
                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-500">
                  {entry.date}
                </td>
                <td className="px-4 py-3">
                  <CategoryBadge category={entry.category} />
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">{pickLang(entry.title, lang)}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {pickLang(entry.detail, lang)}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={entry.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
